import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RecycleItem, RecycleItemType } from '@katsquare/types';
import { Button, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { emptyRecycleBin, listRecycleBin, purgeItem, restoreItem } from '../lib/api';
import { formatBytes } from '../lib/format';

const TYPE_LABEL: Record<RecycleItemType, string> = {
  workspace: 'Workspace',
  magazine: 'Magazine',
  month: 'Month',
  section: 'PDF',
  version: 'Version',
};

export function RecyclePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const items = useQuery({ queryKey: ['recycleBin'], queryFn: listRecycleBin });
  const [q, setQ] = useState('');
  const [emptying, setEmptying] = useState(false);

  const query = q.trim().toLowerCase();
  const filtered = (items.data ?? []).filter(
    (it) =>
      !query ||
      it.name.toLowerCase().includes(query) ||
      (it.context ?? '').toLowerCase().includes(query),
  );

  const refreshTree = () => {
    queryClient.invalidateQueries({ queryKey: ['recycleBin'] });
    queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
  };

  const empty = useMutation({
    mutationFn: emptyRecycleBin,
    onSuccess: () => {
      setEmptying(false);
      refreshTree();
      toast('Recycle bin emptied');
    },
    onError: (e: Error) => toast(e.message),
  });

  const hasItems = (items.data?.length ?? 0) > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-neutral-200 bg-white px-4 py-3 sm:px-6 sm:py-3.5">
        <h1 className="text-lg font-semibold tracking-tight">Recycle bin</h1>
        <div className="relative ml-auto w-full sm:w-44">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the bin…"
            className="w-full rounded-md border border-neutral-200 py-1.5 pl-3 pr-8 text-sm outline-none focus:border-neutral-400"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              title="Clear search"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            >
              ✕
            </button>
          )}
        </div>
        {hasItems &&
          (emptying ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600">Empty the whole bin?</span>
              <Button
                type="button"
                variant="danger"
                onClick={() => empty.mutate()}
                disabled={empty.isPending}
                className="px-2 py-1 text-xs"
              >
                {empty.isPending ? <Spinner /> : 'Empty'}
              </Button>
              <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEmptying(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEmptying(true)}
              className="border border-neutral-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
            >
              Empty bin
            </Button>
          ))}
        <Link to="/app" className="text-sm text-neutral-500 hover:text-neutral-900">
          ← Back
        </Link>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-3xl">
          <div className="mb-4 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs leading-relaxed text-neutral-500">
            <span className="font-medium text-neutral-700">How the bin works.</span> Deleted PDFs and
            versions are kept here as a grace buffer, up to <strong>10% of your plan's storage</strong>.
            A file larger than that is deleted permanently instead; when the bin fills up the oldest
            items are removed to make room. Everything here is cleared automatically after{' '}
            <strong>30 days</strong>.
          </div>

          {items.isPending ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : !hasItems ? (
            <p className="py-16 text-center text-sm text-neutral-400">The recycle bin is empty.</p>
          ) : filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-neutral-400">No deleted items match “{q}”.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {filtered.map((it) => (
                <RecycleRow key={`${it.type}:${it.id}`} item={it} onChanged={refreshTree} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function RecycleRow({ item, onChanged }: { item: RecycleItem; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const restore = useMutation({ mutationFn: () => restoreItem(item.type, item.id), onSuccess: onChanged });
  const purge = useMutation({ mutationFn: () => purgeItem(item.type, item.id), onSuccess: onChanged });

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="w-20 shrink-0 rounded bg-neutral-100 px-2 py-0.5 text-center text-[11px] font-medium text-neutral-500">
        {TYPE_LABEL[item.type]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">{item.name}</p>
        <p className="truncate text-xs text-neutral-400">
          {item.context ? `${item.context} · ` : ''}
          {item.sizeBytes != null ? `${formatBytes(item.sizeBytes)} · ` : ''}deleted{' '}
          {new Date(item.deletedAt).toLocaleString()}
        </p>
      </div>
      {confirming ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-red-600">Delete forever?</span>
          <Button
            type="button"
            variant="danger"
            onClick={() => purge.mutate()}
            disabled={purge.isPending}
            className="px-2 py-1 text-xs"
          >
            {purge.isPending ? <Spinner /> : 'Delete'}
          </Button>
          <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => restore.mutate()}
            disabled={restore.isPending}
            className="px-3 py-1 text-xs"
          >
            {restore.isPending ? <Spinner /> : 'Restore'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirming(true)}
            className="px-2 py-1 text-xs text-red-600 hover:bg-red-50"
          >
            Delete forever
          </Button>
        </div>
      )}
    </li>
  );
}
